// Work mode's pages besides a board and Home (M2-07; Work UX pass 2026-10-20 "one click to the
// work"; Work OS my-tasks.tsx, location-header.tsx; Home is work-home.tsx since WORK-OS):
// - My tasks (`/work/my`): the caller's open tasks in Asana's sections, Today / Overdue / This
//   week / Later / No date; status, assignees, due date and priority edit in the row; quick add
//   in every section files the task on the board opened last (else the first of the person's
//   tasks), on the person's seat, dated for the section; `c` opens New task. Tasks belong to
//   seats: a person who holds no seat has none, and the page says so.
// - A space (`/work/s/{id}`): its open tasks grouped by board (one call, `tasks.list`), each group
//   with "+ Add task"; New board in the header.
// Every screen has Work OS's 56 px location header (no eyebrow, no 28 px title, no subtitle) and
// opens a task in the drawer (`?task=`) without leaving the page.
import {
  Badge,
  Button,
  EmptyState,
  Kbd,
  ListView,
  type ListViewGroup,
  LocationHeader,
  SearchField,
  Segmented,
  Skeleton,
  Toolbar,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
  ViewTabs,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  BlocksIcon,
  CalendarDaysIcon,
  EllipsisIcon,
  FileTextIcon,
  LayoutListIcon,
  ListTodoIcon,
  PaperclipIcon,
  PlusIcon,
  SquareKanbanIcon,
  StarIcon,
} from "lucide-react";
import { type ReactNode, useMemo, useRef, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { type CompanyAccess, isOperatorOnly } from "../company";
import { OperatorOnlyNotice } from "../operator-only";
import { LoadError } from "../screen-parts";
import { type Favorite, useFavorites } from "./arrangement";
import { ClientHealthCard } from "./client-health";
import { AssigneeMenu, DueMenu, PriorityMenu, StatusMenu, useTaskWrites } from "./inline-editors";
import { taskIdOf, useCreateKey, useListKeys } from "./list-keys";
import {
  bucketDue,
  doneStatus,
  dueInstant,
  isDone,
  localDay,
  MY_BUCKETS,
  type MyBucket,
  myBucket,
  openStatus,
  taskRef,
  toSummary,
  useSeatPeople,
} from "./model";
import { type GroupBy, readGroupBy, writeGroupBy } from "./my-sections";
import { useMySections, useSectionGroups } from "./my-task-sections";
import { NewDocDialog } from "./page-dialogs";
import { PlaceFiles } from "./place-files";
import { useRecordRecent } from "./recents";
import { type NewPageTarget, SPACE_ICON } from "./spaces-tree";
import { useTaskRowActions } from "./task-row-actions";
import type { Board, TaskCard } from "./view-engine";
import { NewBoardDialog, NewTaskDialog, SpaceMenu } from "./work-dialogs";
import { readRecentBoards } from "./work-landing";
import {
  boardsQuery,
  companyTasksQuery,
  myTasksQuery,
  spacesQuery,
  workLiveQuery,
} from "./work-queries";

type OpenTask = (id: string) => void;

/** Work pages render only on an API that serves Work; otherwise one honest page. */
export function WorkLive({
  api,
  company,
  children,
}: {
  api: Api;
  company: CompanyAccess;
  children: ReactNode;
}) {
  const live = useQuery(workLiveQuery(api, company.id));
  if (live.isPending)
    return (
      <div
        role="status"
        aria-label="Loading Work"
        className="flex max-w-3xl flex-col gap-3 px-6 py-4"
      >
        <Skeleton className="h-9 w-1/3" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  if (live.isError)
    return (
      <div className="px-6 py-4">
        <LoadError what="Work" error={live.error} />
      </div>
    );
  if (!live.data)
    return (
      <div className="px-6 py-4">
        <EmptyState
          icon={SquareKanbanIcon}
          title="Work is not live on this server yet"
          titleAs="h1"
          data-page="not-live"
          description="Spaces, boards and tasks arrive with the boards build (M2-03). Nothing is lost: this page fills in once the server serves them."
        />
      </div>
    );
  return <>{children}</>;
}

/** "Recruiting › Hiring pipeline" for a task shown outside its board. */
function useLocations(api: Api, co: string) {
  const spaces = useQuery(spacesQuery(api, co));
  const boards = useQuery(boardsQuery(api, co));
  const boardOf = (id: string) => boards.data?.data.find((b) => b.id === id);
  const locationOf = (boardId: string) => {
    const b = boardOf(boardId);
    const s = spaces.data?.data.find((x) => x.id === b?.space_id);
    return b ? (s ? `${s.name} › ${b.name}` : b.name) : "";
  };
  return { spaces, boards, boardOf, locationOf };
}

/** The gutters of a Work body under the location header (the main region is edge to edge). */
export const BODY = "flex min-w-0 flex-1 flex-col px-6 max-md:px-4";

/**
 * A list of task cards from several boards as `ListView` groups with inline status and assignee
 * editors, `j` / `k` / `Enter` and the done toggle. The caller groups and sorts.
 */
export function TaskList({
  api,
  company,
  groups,
  taskId,
  onOpenTask,
  onAddTask,
  boardOf,
  locationOf,
  header = true,
  empty,
  move,
  onTaskDrop,
  ...props
}: {
  api: Api;
  company: CompanyAccess;
  groups: { id: string; title: string; tasks: TaskCard[]; actions?: ReactNode }[];
  taskId: string | undefined;
  onOpenTask: OpenTask;
  onAddTask?: ((groupId: string, title: string) => void) | undefined;
  boardOf: (boardId: string) => Board | undefined;
  locationOf?: ((boardId: string) => string) | undefined;
  header?: boolean;
  empty?: ReactNode;
  /** WOS-BUNDLE: My Tasks in sections — the rows' "Move to…" and the drop between sections. */
  move?: Parameters<typeof useTaskRowActions>[2]["move"];
  onTaskDrop?: ((taskId: string, groupId: string, toIndex: number) => void) | undefined;
  "aria-label": string;
}) {
  const people = useSeatPeople(api, company.id);
  const writes = useTaskWrites(api, company);
  // WOS-BUNDLE: each row's timer and "…" menu (Open, Duplicate…, Move to…).
  const row = useTaskRowActions(api, company, { onOpenTask, move });
  const today = localDay(new Date());
  const root = useRef<HTMLDivElement>(null);
  const byId = useMemo(
    () => new Map(groups.flatMap((g) => g.tasks).map((t) => [t.id, t])),
    [groups],
  );
  const keys = useMemo(
    () => ({
      onOpen: (row: HTMLElement) => {
        const id = taskIdOf(row);
        if (id) onOpenTask(id);
      },
    }),
    [onOpenTask],
  );
  useListKeys(root, keys);
  const views: ListViewGroup[] = groups.map((g) => ({
    id: g.id,
    title: g.title,
    name: g.title,
    ...(g.actions ? { actions: g.actions } : {}),
    tasks: g.tasks.map((t) =>
      toSummary(t, {
        board: boardOf(t.board_id),
        people,
        today,
        ...(locationOf ? { location: locationOf(t.board_id) } : {}),
      }),
    ),
  }));
  return (
    <div ref={root} className="min-w-0">
      <ListView
        {...props}
        groups={views}
        columns={["assignees", "due", "priority"]}
        header={header}
        {...(taskId ? { activeTaskId: taskId } : {})}
        onTaskOpen={onOpenTask}
        onToggleDone={(id, done) => {
          const t = byId.get(id);
          const board = boardOf(t?.board_id ?? "");
          const status = done ? doneStatus(board) : openStatus(board);
          if (status) writes.setStatus(id, status);
        }}
        {...(onAddTask ? { onAddTask } : {})}
        {...(onTaskDrop ? { onTaskDrop } : {})}
        cells={{
          actions: row.actions,
          status: (s) => {
            const t = byId.get(s.id);
            return t ? (
              <StatusMenu
                board={boardOf(t.board_id)}
                status={t.status}
                onChange={(status) => writes.setStatus(t.id, status)}
              />
            ) : null;
          },
          assignees: (s) => {
            const t = byId.get(s.id);
            return t ? (
              <AssigneeMenu
                people={people}
                seatIds={t.assignee_seat_ids}
                title={t.title}
                onToggle={(seat, on) => writes.toggleAssignee(t.id, seat, on)}
              />
            ) : null;
          },
          // WORK-OS: due and priority edit in the row too.
          due: (s) => {
            const t = byId.get(s.id);
            return t ? (
              <DueMenu
                task={t}
                today={today}
                overdue={s.overdue ?? false}
                onChange={(day) => writes.update(t.id, { due_at: day ? dueInstant(day) : null })}
              />
            ) : null;
          },
          priority: (s) => {
            const t = byId.get(s.id);
            return t ? (
              <PriorityMenu task={t} onChange={(p) => writes.update(t.id, { priority: p })} />
            ) : null;
          },
        }}
        {...(empty ? { empty } : {})}
      />
      {row.dialog}
    </div>
  );
}

function NewTaskButton({ onClick }: { onClick: () => void }) {
  return (
    <Button size="sm" aria-keyshortcuts="c" onClick={onClick}>
      <PlusIcon aria-hidden="true" />
      New task
      <Kbd aria-hidden="true" className="ml-1 max-md:hidden">
        c
      </Kbd>
    </Button>
  );
}

/** The star on a space's, board's, doc's, task's or channel's header (WOS-BUNDLE: on the
 * server, at once, undone with a toast on a refusal). */
export function FavoriteStar({
  company,
  favorite,
  name,
}: {
  company: CompanyAccess;
  favorite: Favorite;
  name: string;
}) {
  const favorites = useFavorites(company.id);
  const on = favorites.has(favorite);
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-pressed={on}
            aria-label={on ? `Remove ${name} from favorites` : `Add ${name} to favorites`}
            onClick={() => favorites.toggle(favorite, name)}
          />
        }
      >
        {on ? (
          <StarIcon aria-hidden="true" className="fill-current text-warning" />
        ) : (
          <StarIcon aria-hidden="true" />
        )}
      </TooltipTrigger>
      <TooltipContent>{on ? "Remove from favorites" : "Add to favorites"}</TooltipContent>
    </Tooltip>
  );
}

export function MyTasks({
  api,
  company,
  taskId,
  onOpenTask,
}: {
  api: Api;
  company: CompanyAccess;
  taskId: string | undefined;
  onOpenTask: OpenTask;
}) {
  const co = company.id;
  // OPERATOR-ONLY-UX: no person membership, so no seats, tasks or sections of one's own: one
  // notice instead of the list, and nothing the API would refuse is requested. New task stays
  // (the operator may add a task to a board, company#admin).
  const operatorOnly = isOperatorOnly(company);
  const people = useSeatPeople(api, co);
  const { boards, boardOf, locationOf } = useLocations(api, co);
  const mine = useQuery({ ...myTasksQuery(api, co), enabled: !operatorOnly });
  const writes = useTaskWrites(api, company);
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  useCreateKey(() => setAdding(true));
  const today = localDay(new Date());
  const q = search.trim().toLowerCase();
  const all = (mine.data?.data ?? []).filter((t) => !isDone(t, boardOf(t.board_id)));
  const tasks = all.filter((t) => !q || t.title.toLowerCase().includes(q) || `#${t.number}` === q);
  // Asana's sections: Today first (the day's work), then what slipped, the week, later, undated.
  // Sections with nothing in them stay, each with its quick add, so the day can be planned.
  const groups = MY_BUCKETS.map((g) => ({
    id: g.id,
    title: g.title,
    tasks: tasks
      .filter((t) => myBucket(t.due_at, today) === g.id)
      .sort((a, b) => (a.due_at ?? "").localeCompare(b.due_at ?? "") || a.priority - b.priority),
  })).filter((g) => g.tasks.length || (!q && g.id !== "overdue"));
  // WOS-BUNDLE: Due date (the buckets above) or Sections (the person's own arrangement), kept in
  // this browser; Sections by default. An API without sections shows the buckets.
  const [groupBy, setGroupByState] = useState<GroupBy>(readGroupBy);
  const setGroupBy = (v: GroupBy) => {
    setGroupByState(v);
    writeGroupBy(v);
  };
  const sections = useMySections(api, company);
  const sectioned = useSectionGroups(sections, tasks);
  const bySections = groupBy === "sections" && sections.data !== null;
  const shown = bySections ? sectioned.groups.filter((g) => g.tasks.length || !q) : groups;
  const openCount = mine.data ? all.length : undefined;
  // Quick add files on the board opened last, else the first board of the person's tasks, else
  // the company's first; on the person's first seat, so it shows here at once.
  const liveBoards = (boards.data?.data ?? []).filter((b) => b.archived_at === null);
  const quickBoard =
    readRecentBoards(company.slug)
      .map((id) => liveBoards.find((b) => b.id === id))
      .find((b): b is Board => Boolean(b)) ??
    liveBoards.find((b) => all.some((t) => t.board_id === b.id)) ??
    liveBoards[0];
  const quickAdd = (bucket: string, title: string) => {
    if (!quickBoard) return;
    const due = bySections ? undefined : bucketDue(bucket as MyBucket, today);
    writes.create(
      {
        board_id: quickBoard.id,
        title,
        ...(due ? { due_at: dueInstant(due) } : {}),
        ...(people.mine.length ? { assignee_seat_ids: people.mine.slice(0, 1) } : {}),
      },
      {
        onSuccess: (t) => {
          toast.add({
            title: `${taskRef(t)} created in ${quickBoard.name}`,
            description: t.title,
            type: "success",
          });
          // In sections, the new task lands in the section it was added to.
          if (bySections && bucket !== "recent")
            sections.place.mutate({ taskId: t.id, section: bucket });
        },
      },
    );
  };
  return (
    <div data-screen="my-tasks" className="flex min-h-full min-w-0 flex-col">
      <LocationHeader
        icon={ListTodoIcon}
        title="My tasks"
        {...(openCount !== undefined ? { count: openCount } : {})}
        actions={<NewTaskButton onClick={() => setAdding(true)} />}
      />
      {operatorOnly ? (
        <div className={BODY}>
          <OperatorOnlyNotice slug={company.slug} className="mt-4 max-w-2xl" />
        </div>
      ) : (
        <div className={BODY}>
          <Toolbar aria-label="My tasks tools">
            <SearchField
              value={search}
              placeholder="Search my tasks…"
              aria-label="Search my tasks"
              onChange={(e) => setSearch(e.target.value)}
            />
            {sections.data ? (
              <Segmented<GroupBy>
                size="sm"
                aria-label="Group my tasks by"
                value={groupBy}
                onValueChange={setGroupBy}
                options={[
                  { value: "sections", label: "Sections", icon: LayoutListIcon },
                  { value: "due", label: "Due date", icon: CalendarDaysIcon },
                ]}
              />
            ) : null}
            <span
              className="shrink-0 text-xs text-muted-foreground tabular-nums"
              aria-live="polite"
            >
              {mine.data ? `${tasks.length} open` : ""}
            </span>
          </Toolbar>
          {mine.isPending || (groupBy === "sections" && sections.query.isPending) ? (
            <div role="status" aria-label="Loading your tasks" className="mt-2 flex flex-col gap-2">
              <Skeleton className="h-9" />
              <Skeleton className="h-9" />
              <Skeleton className="h-9" />
            </div>
          ) : mine.isError ? (
            <LoadError what="Your tasks" error={mine.error} />
          ) : all.length || (!q && quickBoard && people.mine.length) ? (
            <TaskList
              aria-label="My tasks"
              api={api}
              company={company}
              groups={shown}
              {...(bySections
                ? { move: sectioned.move, ...(q ? {} : { onTaskDrop: sectioned.onTaskDrop }) }
                : {})}
              taskId={taskId}
              onOpenTask={onOpenTask}
              boardOf={boardOf}
              locationOf={locationOf}
              {...(quickBoard && people.mine.length ? { onAddTask: quickAdd } : {})}
              empty={
                <EmptyState
                  variant="compact"
                  icon={ListTodoIcon}
                  title="No task matches"
                  description="Clear the search to see every open task."
                />
              }
            />
          ) : (
            <EmptyState
              variant="compact"
              icon={ListTodoIcon}
              title={q ? "No task matches" : "Nothing on your seats"}
              description={
                q
                  ? "Clear the search to see every open task."
                  : "Tasks are assigned to seats. Work on a seat you hold shows here; a digital employee delegated to it shows beside you. Press c to add a task."
              }
            />
          )}
          {bySections && !mine.isPending ? sectioned.addButton : null}
        </div>
      )}
      {sectioned.dialogs}
      <NewTaskDialog
        key={String(adding)}
        api={api}
        company={company}
        boards={(boards.data?.data ?? []).filter((b) => b.archived_at === null)}
        people={people}
        open={adding}
        onOpenChange={setAdding}
        onCreated={onOpenTask}
      />
    </div>
  );
}

export function SpaceHome({
  api,
  company,
  spaceId,
  taskId,
  onOpenTask,
}: {
  api: Api;
  company: CompanyAccess;
  spaceId: string;
  taskId: string | undefined;
  onOpenTask: OpenTask;
}) {
  const co = company.id;
  const people = useSeatPeople(api, co);
  const { spaces, boards, boardOf } = useLocations(api, co);
  useRecordRecent(api, co, "space", spaceId);
  const tasks = useQuery(companyTasksQuery(api, co));
  const writes = useTaskWrites(api, company);
  const [adding, setAdding] = useState(false);
  const [newPage, setNewPage] = useState<NewPageTarget | null>(null);
  const [tab, setTab] = useState<"tasks" | "files">("tasks");
  useCreateKey(() => setAdding(true));
  const space = spaces.data?.data.find((s) => s.id === spaceId);
  const own = (boards.data?.data ?? []).filter(
    (b) => b.space_id === spaceId && b.archived_at === null,
  );
  if (spaces.isPending || boards.isPending)
    return (
      <div
        role="status"
        aria-label="Loading the space"
        className="flex max-w-3xl flex-col gap-3 px-6 py-4"
      >
        <Skeleton className="h-9 w-1/3" />
        <Skeleton className="h-32" />
      </div>
    );
  if (spaces.isError || !space)
    return (
      <div className="px-6 py-4">
        <EmptyState
          icon={BlocksIcon}
          title="This space is not available"
          titleAs="h1"
          description="It was archived, or you are not a member of it."
          actions={
            <Button
              variant="secondary"
              render={
                <Link to="/c/$slug/work/my" params={{ slug: company.slug }} search={keepFocus} />
              }
            >
              My tasks
            </Button>
          }
        />
      </div>
    );
  const Icon = SPACE_ICON[space.kind];
  const kindLabel = {
    team: "Team",
    project: "Project",
    client: "Client",
    personal: "Personal",
  }[space.kind];
  const groups = own.map((b) => ({
    id: b.id,
    title: b.name,
    tasks: (tasks.data?.data ?? [])
      .filter((t) => t.board_id === b.id && !isDone(t, b))
      .sort(
        (a, c) => (a.due_at ?? "9999").localeCompare(c.due_at ?? "9999") || a.number - c.number,
      ),
  }));
  return (
    <div data-screen="space-home" className="flex min-h-full min-w-0 flex-col">
      <LocationHeader
        icon={Icon}
        title={space.name}
        badge={
          <>
            <Badge variant="neutral">
              {kindLabel}
              {space.is_private ? " · private" : ""}
            </Badge>
            <FavoriteStar
              company={company}
              favorite={{ kind: "space", id: space.id }}
              name={space.name}
            />
          </>
        }
        actions={
          <>
            {space.archived_at ? null : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setNewPage({ spaceId: space.id, parentPageId: null })}
              >
                <FileTextIcon aria-hidden="true" />
                New doc
              </Button>
            )}
            <NewBoardDialog
              api={api}
              company={company}
              spaceId={space.id}
              trigger={
                <Button variant="ghost" size="sm">
                  <PlusIcon aria-hidden="true" />
                  New board
                </Button>
              }
            />
            {own.length ? <NewTaskButton onClick={() => setAdding(true)} /> : null}
            {/* WOS-BUNDLE: the space's menu (Duplicate…, Rename, Archive) on its header too. */}
            <SpaceMenu
              api={api}
              company={company}
              space={space}
              trigger={
                <Button variant="ghost" size="icon-sm" aria-label="Space actions">
                  <EllipsisIcon aria-hidden="true" />
                </Button>
              }
            />
          </>
        }
      />
      <NewDocDialog api={api} company={company} target={newPage} onClose={() => setNewPage(null)} />
      <div className={BODY}>
        {/* BIZ-CLIENT-HEALTH: a client space's health card on top. */}
        {space.kind === "client" ? (
          <ClientHealthCard api={api} company={company} space={space} />
        ) : null}
        {/* TASK-FILES: the space's tasks, or every file on them. */}
        <ViewTabs
          aria-label="Space tabs"
          className="mb-2"
          value={tab}
          onValueChange={(v) => setTab(v as typeof tab)}
          tabs={[
            { value: "tasks", label: "Tasks", icon: ListTodoIcon },
            { value: "files", label: "Files", icon: PaperclipIcon },
          ]}
        />
        {tab === "files" ? (
          <PlaceFiles
            api={api}
            company={company}
            kind="space"
            id={space.id}
            onOpenTask={onOpenTask}
          />
        ) : tasks.isPending ? (
          <div
            role="status"
            aria-label="Loading the space's tasks"
            className="mt-2 flex flex-col gap-2"
          >
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        ) : tasks.isError ? (
          <LoadError what="The space's tasks" error={tasks.error} />
        ) : own.length ? (
          <TaskList
            aria-label={`${space.name} tasks`}
            api={api}
            company={company}
            groups={groups}
            taskId={taskId}
            onOpenTask={onOpenTask}
            boardOf={boardOf}
            onAddTask={(boardId, title) => writes.create({ board_id: boardId, title })}
          />
        ) : (
          <EmptyState
            variant="compact"
            icon={SquareKanbanIcon}
            title="No boards yet"
            description="A board holds this space's tasks; New board makes one with its home channel."
          />
        )}
      </div>
      <NewTaskDialog
        key={String(adding)}
        api={api}
        company={company}
        boards={own}
        people={people}
        open={adding}
        onOpenChange={setAdding}
        onCreated={onOpenTask}
      />
    </div>
  );
}
